import {
  AppBar,
  Box,
  Button,
  Chip,
  createStyles,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core"
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js"
import clsx from "clsx"
import {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { BounceLoader } from "react-spinners"
import { useGetClientDataQuery } from "../../generated/graphql"
import { useAIServices } from "../../hooks/useAIServices"
import { useClientConnectionStatus } from "../../hooks/useClientConnectionStatus"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import {
  buildClientDetailPath,
  clientDetailSearchWithoutNav,
  clientDetailPastSessionsSearch,
  clientDetailPastSessionsTab,
  clientDetailSessionId,
  PAST_SESSIONS_TAB_TO_URL,
  PAST_SESSIONS_URL_TO_TAB,
  useNavigation,
} from "../../hooks/useNavigation"
import { useSession } from "../../hooks/useSession"
import useTranscriptRecorder from "../../hooks/useTranscriptRecorder"
import { useUser } from "../../hooks/useUser"
import { useVirasafeMetrics } from "../../hooks/useVirasafeMetrics"
import { generatePdf } from "../../utils/generatePdf"
import {
  createManualTranscriptSession,
  createTranscriptUploadSession,
  fetchTranscriptSessions,
  formatDefaultTranscriptTitle,
  TranscriptSessionModality,
} from "../../utils/transcriptApi"
import { colors } from "../../utils/theme"
import ChevronLeftIcon from "../atoms/ChevronLeftIcon"
import { MoodTrendsActivityStatRow } from "../atoms/MoodTrendsActivityStatRow"
import { InviteCodeStatus, Role } from "../../utils/constants"
import ClientMoreButton from "../molecules/ClientMoreButton"
import { HeaderHeight } from "../molecules/Header"
import { InsightSummaryActivityView } from "../molecules/InsightSummaryActivityView"
import MoodHelpIcon from "../molecules/MoodHelpIcon"
import PractitionerDisconnectedHelpIcon from "../molecules/AccountDeletedHelpIcon"
import TranscriptConsentPopover from "../molecules/TranscriptConsentPopover"
import TranscriptSessionControls from "../molecules/TranscriptSessionControls"
import { a11yPropsForTabs, TabPanel } from "../molecules/Tabs"
import AI from "../organisms/AI"
import AssessmentOverview from "../organisms/assessmentOverview"
import AccountSettings from "./AccountSettings"
import ClientMobileSettings from "../organisms/ClientMobileSettings"
import EmotionalCooldownRatings from "../organisms/EmotionalCooldownRatings"
import Insights from "../organisms/Insights/Insights"
import LoopMoodTrends from "../organisms/LoopMoodTrends"
import MoodBarChart from "../organisms/moodBarChart/MoodBarChart"
import PostSessionCarePlan from "../organisms/PostSessionCarePlan"
import PostSessionProgressNote from "../organisms/PostSessionProgressNote"
import ClientDetailChrome, {
  DEFAULT_LOOP_NAV,
  getClientDetailPaneChrome,
  isLoopNavId,
  ClientDetailComingSoon,
  parseLoopNavId,
  type LoopNavId,
} from "../organisms/ClientDetailChrome"
import ClientDetailPrepare from "../organisms/ClientDetailPrepare"
import QuestionnairesOverview from "../organisms/QuestionnairesOverview"
import PlanOverview from "../organisms/safetyPlan/PlanOverview"
import Schedule from "../organisms/Schedule"
import ScheduleOverview from "../organisms/ScheduleOverview"
import TranscriptSessions from "../organisms/transcript/TranscriptSessions"
import ClientCalendar from "../organisms/ClientCalendar"
import Trends from "../organisms/Trends"
import TrendsOverview from "../organisms/TrendsOverview"
import {
  ClientDetailPage,
  ContentCard,
  SectionHeader,
  ViraText,
} from "@ksana/vira-ui"
import { VirasafeMetrics } from "../../utils/types"
import ClientDisconnectedHelpIcon from "../molecules/ClientDisconnectedHelpIcon"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import {
  BRITE_SAFETY_PLAN,
  RELAPSE_PREVENTION_PLAN,
} from "../../utils/constants"

interface Params {
  id: string
  loopTab?: string
}

type ClientTabId =
  | "session-prep"
  | "overview"
  | "safety-plan"
  | "relapse-prevention-plan"
  | "trends"
  | "assessment"
  | "schedule"
  | "transcript"
  | "post-session"
  | "app-info"

type ClientTabDefinition = {
  id: ClientTabId
  label: string
  isVisible: boolean
  renderPanel: () => JSX.Element
}

const ClientDetail = () => {
  const [tab, setTab] = useState<ClientTabId | null>(null)
  const params: Params = useParams()
  const history = useHistory()
  const classes = useStyles()
  const graphqlClient = useGraphQLClient()
  const clientData = useGetClientDataQuery(graphqlClient, {
    clientId: params.id,
  })

  const {
    toDashboard,
    toAccountSettings,
    toMyOrg,
    toTechniquesAndInterventions,
  } = useNavigation()

  const { user } = useUser()
  let userOrgId = "none"
  if (user?.organizations) {
    userOrgId = `${user?.organizations[0].id}-${user?.organizations[0].name}`
  }

  const location = useLocation<{ practitionerId: string }>()
  const practitionerIdFromState = location.state?.practitionerId // supervisor viewing a practitioner's client
  const practitionerIdFromUser = user?.id
  const practitionerId =
    (practitionerIdFromState && practitionerIdFromState.length > 0
      ? practitionerIdFromState
      : practitionerIdFromUser) ?? ""

  const { isAIServicesMember } = useAIServices()
  const [cache, setCache] = useState<string>("")
  const updateCache = (val: any) => setCache(val)
  const metricsData = useVirasafeMetrics(clientData.data?.users_by_pk?.id || "")

  const { session } = useSession()

  const isWebRole = session?.role === Role.WEB_ROLE
  const { status, inviteCode, disconnectionDate, canSchedule } =
    useClientConnectionStatus(params.id, practitionerId)
  const userHasViraPathFeatures =
    clientData.data?.users_by_pk?.users_user_feature_flags?.vira_path_enabled ??
    false

  const getConnectionStatusChip = () => {
    switch (status) {
      case InviteCodeStatus.CONNECTED:
        return (
          <Chip
            label="Connected"
            color="primary"
            className={classes.statusChip}
          />
        )
      case InviteCodeStatus.PENDING:
        return (
          <Chip
            label="Pending"
            color="default"
            className={classes.statusChip}
          />
        )
      case InviteCodeStatus.DECLINED:
        return (
          <Chip
            label="Declined"
            color="secondary"
            className={classes.statusChip}
          />
        )
      case InviteCodeStatus.PRACTITIONER_DISCONNECTED:
        return (
          <Chip
            label="Practitioner Disconnected"
            className={classes.disconnectedChip}
          />
        )
      case InviteCodeStatus.CLIENT_DISCONNECTED:
        return (
          <Chip
            label="Client Disconnected"
            className={classes.disconnectedChip}
          />
        )
      default:
        return (
          <Chip
            label="Unknown"
            color="default"
            className={classes.statusChip}
            style={{ display: "none" }}
          />
        )
    }
  }

  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)

  const partner = queryParams.get("partner")
  const isPartner = partner === "true"
  const sessionIdFromUrl = clientDetailSessionId(search)
  const detailTabFromUrl = (() => {
    const slug = clientDetailPastSessionsTab(search)
    return slug ? PAST_SESSIONS_URL_TO_TAB[slug] : null
  })()

  // Refer to BRD, AI Services Part 1 (Demonstration)
  const preSessionPrompts: any[] = [
    {
      id: 1,
      heading: "Brief Case Overview",
      prompt:
        "Please give me a summary of the client’s presenting goals, history of the symptoms, behavioral data and the treatment plan so far in approximately 300 words.",
    },
    {
      id: 2,
      heading: "Homework Tasks and Adherence Summary",
      prompt:
        "Remind me of the nudge schedule that has been in place since our last session and summarize the client’s rates of completion of the delivered interventions.",
    },
  ]
  const {
    showBriteFeatures,
    showMVP2Features,
    showViraPathFeatures,
    showViraScribe,
    showViraLoop,
    showDBTDiaryFeatures,
  } = useOrganizationFeatureFlags()

  const progressNoteAnchorRef = useRef<HTMLDivElement | null>(null)

  const [preferredTranscriptSessionId, setPreferredTranscriptSessionId] =
    useState<string | null>(() => sessionIdFromUrl)
  const [openPastSessionsOnProgressNote, setOpenPastSessionsOnProgressNote] =
    useState(false)
  const [transcriptRefreshRequestId, setTranscriptRefreshRequestId] =
    useState(0)
  /** When set, the next mic upload attaches to this empty session shell. */
  const [attachRecordingSessionId, setAttachRecordingSessionId] = useState<
    string | null
  >(null)

  const legacyLoopNav = parseLoopNavQueryParam(search)
  const loopNavFromPath = parseLoopNavId(params.loopTab)
  const loopNav: LoopNavId =
    loopNavFromPath ?? legacyLoopNav ?? DEFAULT_LOOP_NAV

  // Deep-link: `?session=` selects that transcript on Past sessions.
  useEffect(() => {
    if (sessionIdFromUrl) {
      setPreferredTranscriptSessionId(sessionIdFromUrl)
    }
  }, [sessionIdFromUrl])

  // Canonicalize Loop tab URLs: path segment wins; migrate ?nav=; default prepare.
  // Keep `?session=` / `?tab=` only on Past sessions.
  useEffect(() => {
    if (!showViraLoop) {
      return
    }
    let targetTab = loopNavFromPath ?? legacyLoopNav ?? DEFAULT_LOOP_NAV
    let forceProgressNoteTab = false
    // Legacy leaf ids → current destinations
    if (targetTab === "new-session") {
      targetTab = "prepare"
    } else if (targetTab === "plan-and-note") {
      targetTab = "past-sessions"
      forceProgressNoteTab = true
      setOpenPastSessionsOnProgressNote(true)
    }
    const baseSearch = clientDetailSearchWithoutNav(search)
    const nextSearch =
      targetTab === "past-sessions"
        ? forceProgressNoteTab
          ? clientDetailPastSessionsSearch(baseSearch, {
              detailTab: "progress-note",
            })
          : baseSearch
        : clientDetailPastSessionsSearch(baseSearch, {
            sessionId: null,
            detailTab: null,
          })
    const desired = buildClientDetailPath(params.id, targetTab, nextSearch)
    const actual = `${history.location.pathname}${history.location.search}`
    if (desired !== actual) {
      history.replace(desired)
    }
  }, [showViraLoop, params.id, loopNavFromPath, legacyLoopNav, search, history])

  const navigateLoop = useCallback(
    (
      itemId: string,
      sessionId?: string | null,
      detailTab?: "scores" | "transcript" | "actionPlan" | "progressNote" | null
    ) => {
      if (!isLoopNavId(itemId)) {
        return
      }
      let target: LoopNavId = itemId
      let forceProgressNoteTab = false
      if (itemId === "new-session") {
        target = "prepare"
      } else if (itemId === "plan-and-note") {
        target = "past-sessions"
        forceProgressNoteTab = true
        setOpenPastSessionsOnProgressNote(true)
      }
      const baseSearch = clientDetailSearchWithoutNav(search)
      if (target !== "past-sessions") {
        history.push(
          buildClientDetailPath(
            params.id,
            target,
            clientDetailPastSessionsSearch(baseSearch, {
              sessionId: null,
              detailTab: null,
            })
          )
        )
        return
      }
      const sessionForUrl =
        sessionId !== undefined
          ? sessionId
          : preferredTranscriptSessionId ?? clientDetailSessionId(search)
      const tabForUrl = forceProgressNoteTab
        ? "progress-note"
        : detailTab
        ? PAST_SESSIONS_TAB_TO_URL[detailTab]
        : clientDetailPastSessionsTab(search) ??
          (openPastSessionsOnProgressNote ? "progress-note" : "action-plan")
      history.push(
        buildClientDetailPath(
          params.id,
          target,
          clientDetailPastSessionsSearch(baseSearch, {
            sessionId: sessionForUrl,
            detailTab: tabForUrl,
          })
        )
      )
    },
    [
      history,
      openPastSessionsOnProgressNote,
      params.id,
      preferredTranscriptSessionId,
      search,
    ]
  )

  const syncPastSessionsUrl = useCallback(
    (next: {
      sessionId: string | null
      detailTab: "scores" | "transcript" | "actionPlan" | "progressNote"
    }) => {
      setPreferredTranscriptSessionId(next.sessionId)
      if (!showViraLoop || loopNav !== "past-sessions") {
        return
      }
      const nextTabSlug = PAST_SESSIONS_TAB_TO_URL[next.detailTab]
      const currentTabSlug = clientDetailPastSessionsTab(search)
      if (
        sessionIdFromUrl === next.sessionId &&
        currentTabSlug === nextTabSlug
      ) {
        return
      }
      history.replace(
        buildClientDetailPath(
          params.id,
          "past-sessions",
          clientDetailPastSessionsSearch(clientDetailSearchWithoutNav(search), {
            sessionId: next.sessionId,
            detailTab: nextTabSlug,
          })
        )
      )
    },
    [history, loopNav, params.id, search, sessionIdFromUrl, showViraLoop]
  )

  // Post-session AI helper card temporarily removed from Plan and note /
  // post-session surfaces (Second Opinion / Self-Care / freeform query).

  const appInsights = useAppInsightsContext()
  // Empty strings are schema placeholders for useTrackEvent; real values are
  // passed at click time via trackTabSelection (tab label, org, practitionerId).
  const trackClientTabs = useTrackEvent(
    appInsights,
    "Client Detail Tab Click",
    {
      tab: "",
      org: "",
      practitionerId: "",
    }
  )

  enum TIME_PERIOD {
    WEEK = "week",
    MONTH = "month",
  }

  const [timePeriod, setTimePeriod] = useState<TIME_PERIOD>(TIME_PERIOD.WEEK)

  const rawClient = clientData.data?.users_by_pk
  const demographicFirstName =
    rawClient?.client_demographics?.[0]?.first_name?.trim()
  const demographicLastName =
    rawClient?.client_demographics?.[0]?.last_name?.trim()

  const client = rawClient
    ? {
        ...rawClient,
        first_name:
          demographicFirstName && demographicFirstName.length > 0
            ? demographicFirstName
            : rawClient.first_name,
        last_name:
          demographicLastName && demographicLastName.length > 0
            ? demographicLastName
            : rawClient.last_name,
      }
    : rawClient

  const firstName = client?.first_name
  const lastName = client?.last_name
  const displayName = client?.display_name

  const [loading, setLoading] = useState(false)
  const [transcriptConsentAnchorEl, setTranscriptConsentAnchorEl] =
    useState<HTMLElement | null>(null)
  const [isTranscriptConsentChecked, setIsTranscriptConsentChecked] =
    useState(false)
  const [transcriptPreflightErrorMessage, setTranscriptPreflightErrorMessage] =
    useState<string | null>(null)
  const [isCheckingTranscriptAccess, setIsCheckingTranscriptAccess] =
    useState(false)
  const [isConsentFileUploading, setIsConsentFileUploading] = useState(false)

  const handleTranscriptSessionCreated = useCallback(
    (sessionId: string) => {
      setPreferredTranscriptSessionId(sessionId)
      setAttachRecordingSessionId(null)
      setTranscriptRefreshRequestId((current) => current + 1)
      if (showViraLoop) {
        navigateLoop("past-sessions", sessionId)
      }
    },
    [navigateLoop, showViraLoop]
  )

  const handleTranscriptSessionProcessingComplete = useCallback(
    (sessionId: string) => {
      setPreferredTranscriptSessionId(sessionId)
      setAttachRecordingSessionId(null)
      setTranscriptRefreshRequestId((current) => current + 1)
    },
    []
  )

  const {
    state: transcriptRecorderState,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    audioMeterFillRef,
    audioVisualizerCanvasRef,
    registerElapsedTimeElement,
  } = useTranscriptRecorder({
    clientId: params.id,
    firstName,
    lastName,
    practitionerId: practitionerId || "",
    jwt: session?.jwt || "",
    attachToSessionId: attachRecordingSessionId,
    onSessionCreated: handleTranscriptSessionCreated,
    onSessionProcessingComplete: handleTranscriptSessionProcessingComplete,
  })

  const exportToPdf = async () => {
    setLoading(true)
    await generatePdf(client?.last_name || "", "Overview")
    setLoading(false)
  }

  if (!client) {
    return (
      <div className={clsx(classes.clientDetail, classes.loading)}>
        Loading...
      </div>
    )
  }

  const preSessionTxt = `Your Vira AI Assistant will help you prepare for your appointment with ${firstName}.`
  const checkingTotal = client.mood_measurements.length
  const totalCheckins = checkingTotal !== undefined ? checkingTotal : 0
  const canStartTranscriptSession = Boolean(
    showViraScribe && practitionerId && session?.jwt
  )
  const isTranscriptBusy =
    transcriptRecorderState.isUploading || transcriptRecorderState.isProcessing
  const blockingTranscriptErrorMessage = transcriptPreflightErrorMessage
  const hasTranscriptRecordingError = Boolean(blockingTranscriptErrorMessage)

  const handleOpenTranscriptConsent = async (
    event: MouseEvent<HTMLElement>,
    options?: { attachToSessionId?: string | null }
  ) => {
    if (isTranscriptBusy) {
      return
    }

    const anchor = event.currentTarget
    if (!(anchor instanceof HTMLElement)) {
      return
    }

    setAttachRecordingSessionId(options?.attachToSessionId ?? null)
    setTranscriptConsentAnchorEl(anchor)
    setIsTranscriptConsentChecked(false)
    setTranscriptPreflightErrorMessage(null)
    setIsCheckingTranscriptAccess(true)

    const TRANSCRIPT_ACCESS_CHECK_TIMEOUT_MS = 10_000

    try {
      await Promise.race([
        fetchTranscriptSessions(session?.jwt || "", {
          clientId: params.id,
          practitionerId: practitionerId || "",
        }),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => {
            reject(
              new Error(
                "Transcript service did not respond in time. Check that transcript-api is running, then try again."
              )
            )
          }, TRANSCRIPT_ACCESS_CHECK_TIMEOUT_MS)
        }),
      ])
      setTranscriptPreflightErrorMessage(null)
    } catch (error) {
      setTranscriptPreflightErrorMessage(
        getTranscriptErrorMessage(
          error,
          "Unable to verify transcript recording access."
        )
      )
    } finally {
      setIsCheckingTranscriptAccess(false)
    }
  }

  const handleCloseTranscriptConsent = () => {
    setTranscriptConsentAnchorEl(null)
    setIsTranscriptConsentChecked(false)
    // Keep attach target while recording is in progress; clear only when idle.
    if (
      !transcriptRecorderState.isRecording &&
      !transcriptRecorderState.isUploading
    ) {
      setAttachRecordingSessionId(null)
    }
  }

  const handleRequestRecordForSession = (
    sessionId: string,
    event: MouseEvent<HTMLElement>
  ) => {
    setPreferredTranscriptSessionId(sessionId)
    void handleOpenTranscriptConsent(event, { attachToSessionId: sessionId })
  }

  const handleConfirmTranscriptConsent = async (
    modality: TranscriptSessionModality
  ) => {
    if (
      !isTranscriptConsentChecked ||
      hasTranscriptRecordingError ||
      isCheckingTranscriptAccess ||
      isTranscriptBusy ||
      isConsentFileUploading
    ) {
      return
    }

    const started = await startRecording(modality)
    if (started) {
      handleCloseTranscriptConsent()
    }
  }

  const handleUploadTranscriptFromConsent = async (
    file: File,
    _modality: TranscriptSessionModality
  ) => {
    if (
      !isTranscriptConsentChecked ||
      hasTranscriptRecordingError ||
      isCheckingTranscriptAccess ||
      isTranscriptBusy ||
      isConsentFileUploading ||
      !session?.jwt ||
      !practitionerId
    ) {
      return
    }

    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!extension || !["pdf", "docx"].includes(extension)) {
      setTranscriptPreflightErrorMessage(
        "Only PDF and DOCX transcript files are supported."
      )
      return
    }

    setIsConsentFileUploading(true)
    setTranscriptPreflightErrorMessage(null)
    try {
      const createdSession = await createTranscriptUploadSession(session.jwt, {
        file,
        clientId: params.id,
        practitionerId,
      })
      handleTranscriptSessionCreated(createdSession.sessionId)
      handleCloseTranscriptConsent()
      if (showViraLoop) {
        navigateLoop("past-sessions", createdSession.sessionId)
      }
    } catch (error) {
      setTranscriptPreflightErrorMessage(
        getTranscriptErrorMessage(error, "Unable to upload transcript.")
      )
    } finally {
      setIsConsentFileUploading(false)
    }
  }

  const handleCreateEmptySessionFromConsent = async (
    modality: TranscriptSessionModality
  ) => {
    if (
      hasTranscriptRecordingError ||
      isCheckingTranscriptAccess ||
      isTranscriptBusy ||
      isConsentFileUploading ||
      !session?.jwt ||
      !practitionerId
    ) {
      return
    }

    setIsConsentFileUploading(true)
    setTranscriptPreflightErrorMessage(null)
    try {
      const title = formatDefaultTranscriptTitle(
        firstName,
        lastName,
        new Date()
      )
      const createdSession = await createManualTranscriptSession(session.jwt, {
        clientId: params.id,
        practitionerId,
        modality,
        title,
      })
      handleTranscriptSessionCreated(createdSession.sessionId)
      handleCloseTranscriptConsent()
      if (showViraLoop) {
        navigateLoop("past-sessions", createdSession.sessionId, "actionPlan")
      }
    } catch (error) {
      setTranscriptPreflightErrorMessage(
        getTranscriptErrorMessage(
          error,
          "Unable to create a plan-only session."
        )
      )
    } finally {
      setIsConsentFileUploading(false)
    }
  }

  const hasDistressRatings = (client.activity_rating?.length ?? 0) > 0
  const showDistressRatings =
    showBriteFeatures || (showViraLoop && hasDistressRatings)

  const renderOverviewPanel = (opts?: { loop?: boolean }) => {
    const isClientDetail = Boolean(opts?.loop)
    return (
      <>
        <TrendsOverview
          client={client}
          openTrends={() => setTab("trends")}
          variant={isClientDetail ? "clientDetail" : "default"}
        />
        <div
          className={clsx(
            classes.chartsAndSchedule,
            isClientDetail && classes.chartsAndScheduleClientDetail
          )}
        >
          <div>
            {isClientDetail ? (
              <SectionHeader
                level="section"
                title="Insights: Mood Trends"
                sx={{ mb: 1 }}
              />
            ) : (
              <div className={clsx(classes.titleIcon, classes.subtitleMargin)}>
                <Typography variant="h3" className={classes.subtitle}>
                  {showBriteFeatures && showMVP2Features
                    ? "Insights: Mood Trends"
                    : "Mood"}
                </Typography>
                <MoodHelpIcon
                  data={client.mood_measurements}
                  timePeriod={timePeriod}
                />
              </div>
            )}
            <div className={classes.chart}>
              {isClientDetail ? (
                <LoopMoodTrends data={client.mood_measurements} />
              ) : (
                <MoodBarChart
                  data={client.mood_measurements}
                  timePeriod={timePeriod}
                  setTimePeriod={setTimePeriod}
                />
              )}
            </div>
            {showDistressRatings && (
              <>
                {isClientDetail ? (
                  <ContentCard title="Distress Ratings" sx={{ mb: 3 }}>
                    <ViraText
                      tone="subtle"
                      sx={{ fontSize: 12, mb: 2, lineHeight: 1.5 }}
                    >
                      Users rate their distress before and after completing an
                      activity (emotional cooldown). 1 = Least distressed, 5 =
                      most distressed
                    </ViraText>
                    <EmotionalCooldownRatings client={client} />
                  </ContentCard>
                ) : (
                  <>
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Distress Ratings
                    </Typography>
                    <Typography
                      variant="body2"
                      className={clsx(
                        classes.subtitle,
                        classes.subtitleMargin,
                        classes.distressRatings
                      )}
                    >
                      Users rate their distress before and after completing an
                      activity (emotional cooldown). 1 = Least distressed, 5 =
                      most distressed
                    </Typography>
                    <EmotionalCooldownRatings client={client} />
                  </>
                )}
              </>
            )}
            {isClientDetail ? (
              <SectionHeader
                level="section"
                title={
                  showBriteFeatures && showMVP2Features
                    ? "Insights: Activity Trends"
                    : "Insights"
                }
              />
            ) : (
              <Typography
                variant="h3"
                className={clsx(classes.subtitle, classes.subtitleMargin)}
              >
                {showBriteFeatures && showMVP2Features
                  ? "Insights: Activity Trends"
                  : "Insights"}
              </Typography>
            )}
            <div className={classes.chart}>
              <Insights checkIns={totalCheckins} client={client} />
            </div>
            {showBriteFeatures && showMVP2Features && (
              <Mvp2ActivityMetrics metricsData={metricsData} />
            )}
            {!isClientDetail ? (
              <QuestionnairesOverview
                client={client}
                canSchedule={canSchedule}
              />
            ) : null}
          </div>
          {!isClientDetail ? (
            <ScheduleOverview client={client} canSchedule={canSchedule} />
          ) : null}
        </div>
        {!isClientDetail ? (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            style={{ paddingTop: "10px" }}
          >
            <Button
              variant="outlined"
              onClick={exportToPdf}
              style={{ textTransform: "none" }}
              disabled={loading}
            >
              Export
            </Button>
          </Grid>
        ) : null}
      </>
    )
  }

  const clientTabs: ClientTabDefinition[] = [
    {
      id: "session-prep",
      label: "SESSION PREP",
      isVisible: isAIServicesMember,
      renderPanel: () => (
        <AI
          client={client}
          userId={practitionerId}
          prompts={preSessionPrompts}
          text={preSessionTxt}
          tab="pre-session"
          updateCache={updateCache}
          cache={cache}
        />
      ),
    },
    {
      id: "overview",
      label: "OVERVIEW",
      isVisible: true,
      renderPanel: () => renderOverviewPanel(),
    },
    {
      id: "safety-plan",
      label: "SAFETY PLAN",
      isVisible: showBriteFeatures,
      renderPanel: () => (
        <PlanOverview client={client} planName={BRITE_SAFETY_PLAN} />
      ),
    },
    {
      id: "relapse-prevention-plan",
      label: "RELAPSE PREVENTION",
      isVisible: showViraPathFeatures && userHasViraPathFeatures,
      renderPanel: () => (
        <PlanOverview client={client} planName={RELAPSE_PREVENTION_PLAN} />
      ),
    },
    {
      id: "trends",
      label: "TRENDS",
      isVisible: true,
      renderPanel: () => <Trends client={client} />,
    },
    {
      id: "assessment",
      label: "ASSESSMENT",
      isVisible: true,
      renderPanel: () => (
        <AssessmentOverview client={client} canSchedule={canSchedule} />
      ),
    },
    {
      id: "schedule",
      label: "SCHEDULE",
      isVisible: true,
      renderPanel: () => <Schedule client={client} canSchedule={canSchedule} />,
    },
    {
      id: "transcript",
      label: "TRANSCRIPT",
      isVisible: canStartTranscriptSession,
      renderPanel: () => (
        <TranscriptSessions
          clientId={client.id}
          practitionerId={practitionerId || ""}
          clientName={firstName || displayName || "this client"}
          practitionerName={
            practitionerId === user?.id
              ? [user?.firstName, user?.lastName].filter(Boolean).join(" ")
              : undefined
          }
          onRequestStartRecording={handleOpenTranscriptConsent}
          onRequestRecordForSession={handleRequestRecordForSession}
          onPauseRecording={pauseRecording}
          onResumeRecording={resumeRecording}
          onStopRecording={stopRecording}
          isRecording={transcriptRecorderState.isRecording}
          isPaused={transcriptRecorderState.isPaused}
          isUploading={transcriptRecorderState.isUploading}
          isProcessing={transcriptRecorderState.isProcessing}
          audioMeterFillRef={audioMeterFillRef}
          registerElapsedTimeElement={registerElapsedTimeElement}
          recorderErrorMessage={transcriptRecorderState.errorMessage}
          refreshRequestId={transcriptRefreshRequestId}
          preferredSessionId={preferredTranscriptSessionId}
          onEditProgressNote={(sessionId) => {
            setPreferredTranscriptSessionId(sessionId)
            setTab("post-session")
          }}
        />
      ),
    },
    {
      id: "post-session",
      label: "POST SESSION",
      isVisible: isAIServicesMember,
      renderPanel: () => (
        <>
          {showViraScribe && practitionerId && session?.jwt ? (
            <>
              <PostSessionCarePlan
                client={client}
                practitionerId={practitionerId}
                enabled={showViraScribe}
                loopEnabled={showViraLoop}
              />
              <PostSessionProgressNote
                client={client}
                practitionerId={practitionerId}
                enabled={showViraScribe}
                preferredTranscriptSessionId={preferredTranscriptSessionId}
              />
            </>
          ) : null}
        </>
      ),
    },
    {
      id: "app-info",
      label: "APP INFO",
      isVisible: true,
      renderPanel: () => <ClientMobileSettings client={client} />,
    },
  ]

  const visibleTabs = clientTabs.filter(({ isVisible }) => isVisible)
  const activeTab =
    visibleTabs.find(({ id }) => id === tab)?.id ?? visibleTabs[0]?.id

  if (!activeTab) {
    return null
  }

  const trackTabSelection = (tabLabel: string) =>
    trackClientTabs({
      tab: tabLabel,
      org: userOrgId,
      practitionerId,
    })

  if (showViraLoop) {
    const clientDisplayName =
      [firstName, lastName].filter(Boolean).join(" ") || displayName || "Client"
    const practitionerDisplayName =
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      "Practitioner"
    const loopChips = [
      ...(showBriteFeatures
        ? [{ id: "virasafe", label: "VIRASAFE", tone: "virasafe" as const }]
        : []),
      ...(showViraPathFeatures && userHasViraPathFeatures
        ? [{ id: "virapath", label: "VIRAPATH", tone: "virapath" as const }]
        : []),
      ...(showDBTDiaryFeatures
        ? [{ id: "dbt", label: "DBT", tone: "dbt" as const }]
        : []),
    ]

    const renderLoopPane = () => {
      const chrome = getClientDetailPaneChrome(loopNav, clientDisplayName)
      const wrap = (content: ReactNode) =>
        chrome ? (
          <ClientDetailPage eyebrow={chrome.eyebrow} title={chrome.title}>
            {content}
          </ClientDetailPage>
        ) : (
          <ClientDetailPage>{content}</ClientDetailPage>
        )

      switch (loopNav) {
        case "prepare":
        case "new-session":
          return (
            <ClientDetailPrepare
              client={client}
              clientName={clientDisplayName}
              clientFirstName={firstName || clientDisplayName}
              practitionerId={practitionerId || ""}
            />
          )
        case "summary":
          return wrap(renderOverviewPanel({ loop: true }))
        case "calendar":
          return wrap(
            <ClientCalendar
              client={client}
              practitionerId={practitionerId || undefined}
              includeSessions={canStartTranscriptSession}
              canSchedule={canSchedule}
              onSessionClick={(sessionId) => {
                setPreferredTranscriptSessionId(sessionId)
                setOpenPastSessionsOnProgressNote(false)
                navigateLoop("past-sessions", sessionId)
              }}
            />
          )
        case "sleep":
          return wrap(<Trends client={client} pane="sleep" />)
        case "activity":
          return wrap(<Trends client={client} pane="activity" />)
        case "language":
          return wrap(<Trends client={client} pane="language" />)
        case "assessments":
          return wrap(
            <AssessmentOverview client={client} canSchedule={canSchedule} />
          )
        case "dbt-diary":
          return showDBTDiaryFeatures ? (
            wrap(
              <AssessmentOverview client={client} canSchedule={canSchedule} />
            )
          ) : (
            <ClientDetailComingSoon
              title="DBT diary cards"
              eyebrow={`${clientDisplayName} · Treat`}
            />
          )
        case "safety-plan":
          return showBriteFeatures ? (
            wrap(<PlanOverview client={client} planName={BRITE_SAFETY_PLAN} />)
          ) : (
            <ClientDetailComingSoon
              title="Safety plan"
              eyebrow={`${clientDisplayName} · Treat`}
            />
          )
        case "relapse":
          return showViraPathFeatures && userHasViraPathFeatures ? (
            wrap(
              <PlanOverview
                client={client}
                planName={RELAPSE_PREVENTION_PLAN}
              />
            )
          ) : (
            <ClientDetailComingSoon
              title="Relapse prevention"
              eyebrow={`${clientDisplayName} · Treat`}
            />
          )
        case "plan-and-note":
        case "past-sessions":
          return canStartTranscriptSession ? (
            wrap(
              <TranscriptSessions
                clientId={client.id}
                practitionerId={practitionerId || ""}
                clientName={firstName || displayName || "this client"}
                practitionerName={
                  practitionerId === user?.id
                    ? [user?.firstName, user?.lastName]
                        .filter(Boolean)
                        .join(" ")
                    : undefined
                }
                onRequestStartRecording={handleOpenTranscriptConsent}
                onRequestRecordForSession={handleRequestRecordForSession}
                onPauseRecording={pauseRecording}
                onResumeRecording={resumeRecording}
                onStopRecording={stopRecording}
                isRecording={transcriptRecorderState.isRecording}
                isPaused={transcriptRecorderState.isPaused}
                isUploading={transcriptRecorderState.isUploading}
                isProcessing={transcriptRecorderState.isProcessing}
                audioMeterFillRef={audioMeterFillRef}
                registerElapsedTimeElement={registerElapsedTimeElement}
                recorderErrorMessage={transcriptRecorderState.errorMessage}
                refreshRequestId={transcriptRefreshRequestId}
                preferredSessionId={preferredTranscriptSessionId}
                preferredDetailTab={detailTabFromUrl}
                openOnProgressNote={openPastSessionsOnProgressNote}
                onPastSessionsStateChange={syncPastSessionsUrl}
                paneMode="history"
                variant="clientDetail"
                renderActionPlan={
                  showViraScribe && practitionerId
                    ? (sessionId, helpers) => (
                        <PostSessionCarePlan
                          client={client}
                          practitionerId={practitionerId}
                          enabled={showViraScribe}
                          loopEnabled
                          embedded
                          hideInputs={helpers.hideInputs}
                          onInputsChange={helpers.onInputsChange}
                          progressNoteGate={helpers.progressNoteGate}
                          preferredTranscriptSessionId={sessionId}
                          onDraftNoteRequest={helpers.openProgressNote}
                          onRequestRecordRecap={helpers.requestRecordForSession}
                          onRequestUploadTranscript={
                            helpers.requestUploadForSession
                          }
                        />
                      )
                    : undefined
                }
                renderProgressNote={
                  showViraScribe && practitionerId
                    ? (sessionId, helpers) => (
                        <div ref={progressNoteAnchorRef}>
                          <PostSessionProgressNote
                            client={client}
                            practitionerId={practitionerId}
                            enabled={showViraScribe}
                            preferredTranscriptSessionId={sessionId}
                            onNoteGateChange={helpers.onNoteGateChange}
                          />
                        </div>
                      )
                    : undefined
                }
              />
            )
          ) : (
            <ClientDetailComingSoon
              title="Sessions"
              eyebrow={`${clientDisplayName} · Treat`}
            />
          )
        case "account-settings":
          return wrap(<AccountSettings embedded />)
        default:
          return wrap(renderOverviewPanel({ loop: true }))
      }
    }

    return (
      <div className={classes.proClientDetail}>
        <ClientDetailChrome
          clientName={clientDisplayName}
          practitionerName={practitionerDisplayName}
          chips={loopChips}
          activeItemId={loopNav}
          visibility={{
            showSafetyPlan: showBriteFeatures,
            showRelapse: showViraPathFeatures && userHasViraPathFeatures,
            showPastSessions: canStartTranscriptSession,
            showPlanAndNote: Boolean(showViraScribe),
            showDbtDiary: showDBTDiaryFeatures,
          }}
          onNavigate={(itemId) => {
            if (itemId === "past-sessions") {
              setOpenPastSessionsOnProgressNote(false)
            }
            navigateLoop(itemId)
            trackTabSelection(`loop:${itemId}`)
          }}
          onBackToClients={isPartner ? () => undefined : toDashboard}
          onStartSession={
            canStartTranscriptSession ? handleOpenTranscriptConsent : undefined
          }
          onPauseSession={pauseRecording}
          onResumeSession={resumeRecording}
          onStopSession={stopRecording}
          sessionStatus={
            transcriptRecorderState.isUploading
              ? "uploading"
              : transcriptRecorderState.isProcessing
              ? "processing"
              : transcriptRecorderState.isPaused
              ? "paused"
              : transcriptRecorderState.isRecording
              ? "recording"
              : "idle"
          }
          registerElapsedTimeElement={registerElapsedTimeElement}
          audioVisualizerCanvasRef={audioVisualizerCanvasRef}
          onHeaderNav={(id) => {
            if (id === "clients") toDashboard()
            else if (id === "org") toMyOrg()
            else if (id === "techniques") toTechniquesAndInterventions()
            else if (id === "account") navigateLoop("account-settings")
          }}
          onAccountSettings={() => navigateLoop("account-settings")}
        >
          <TranscriptConsentPopover
            anchorEl={transcriptConsentAnchorEl}
            isConsentChecked={isTranscriptConsentChecked}
            isCheckingAccess={isCheckingTranscriptAccess}
            errorMessage={blockingTranscriptErrorMessage}
            isBusy={isTranscriptBusy}
            isFileUploading={isConsentFileUploading}
            onConsentCheckedChange={setIsTranscriptConsentChecked}
            onClose={handleCloseTranscriptConsent}
            onConfirm={handleConfirmTranscriptConsent}
            onUploadFile={handleUploadTranscriptFromConsent}
            onCreateEmptySession={handleCreateEmptySessionFromConsent}
          />
          {renderLoopPane()}
        </ClientDetailChrome>
      </div>
    )
  }

  return (
    <div className={classes.clientDetail}>
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 5000,
        }}
        id="spinner"
      >
        <BounceLoader
          color={"rgb(117, 145, 228)"}
          size={75}
          loading={loading}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <div style={{ display: loading ? "block" : "none" }}>
          Generating PDF
        </div>
      </div>
      <div className={classes.header}>
        <TranscriptConsentPopover
          anchorEl={transcriptConsentAnchorEl}
          isConsentChecked={isTranscriptConsentChecked}
          isCheckingAccess={isCheckingTranscriptAccess}
          errorMessage={blockingTranscriptErrorMessage}
          isBusy={isTranscriptBusy}
          isFileUploading={isConsentFileUploading}
          onConsentCheckedChange={setIsTranscriptConsentChecked}
          onClose={handleCloseTranscriptConsent}
          onConfirm={handleConfirmTranscriptConsent}
          onUploadFile={handleUploadTranscriptFromConsent}
          onCreateEmptySession={handleCreateEmptySessionFromConsent}
        />
        <div className={classes.headerTopRow}>
          <div className={classes.titleContainer}>
            <button
              className={classes.navigation}
              onClick={isPartner ? () => {} : toDashboard}
            >
              <ChevronLeftIcon
                className={isPartner ? classes.partner : classes.chevron}
              />
              <Typography
                variant="h2"
                className={isPartner ? classes.partnerTitle : classes.title}
              >
                {firstName} {lastName}
              </Typography>
            </button>
          </div>
          <div className={classes.headerActions}>
            {canStartTranscriptSession && (
              <TranscriptSessionControls
                isRecording={transcriptRecorderState.isRecording}
                isPaused={transcriptRecorderState.isPaused}
                isUploading={transcriptRecorderState.isUploading}
                isProcessing={transcriptRecorderState.isProcessing}
                onStartRecording={handleOpenTranscriptConsent}
                onPauseRecording={pauseRecording}
                onResumeRecording={resumeRecording}
                onStopRecording={stopRecording}
                registerElapsedTimeElement={registerElapsedTimeElement}
              />
            )}
            {!isWebRole && (
              <div className={classes.ellipsisIcon}>
                <ClientMoreButton
                  code={inviteCode?.code || ""}
                  status={status || InviteCodeStatus.PENDING}
                  clientId={client.id}
                  firstName={firstName || ""}
                  lastName={lastName || ""}
                  displayName={displayName || ""}
                  partner={isPartner}
                  clientDetail={true}
                />
              </div>
            )}
          </div>
        </div>
        {/* Disconnection Status */}
        <Box className={classes.connectionStatusContainer}>
          {status && status !== InviteCodeStatus.CONNECTED && (
            <>
              {getConnectionStatusChip()}{" "}
              {status === InviteCodeStatus.PRACTITIONER_DISCONNECTED && (
                <PractitionerDisconnectedHelpIcon
                  ended_at={
                    disconnectionDate ? disconnectionDate.toISOString() : ""
                  }
                />
              )}
              {status === InviteCodeStatus.CLIENT_DISCONNECTED && (
                <ClientDisconnectedHelpIcon
                  ended_at={
                    disconnectionDate ? disconnectionDate.toISOString() : ""
                  }
                />
              )}
            </>
          )}
        </Box>

        <AppBar position="static" className={classes.tabs}>
          <Tabs
            value={activeTab}
            onChange={(event: ChangeEvent<{}>, newValue: ClientTabId) =>
              setTab(newValue)
            }
            variant="scrollable"
            scrollButtons="auto"
            aria-label="tabs"
          >
            {visibleTabs.map(({ id, label }, index) => (
              <Tab
                key={id}
                value={id}
                label={label}
                {...a11yPropsForTabs(index)}
                onClick={() => trackTabSelection(label)}
              />
            ))}
          </Tabs>
        </AppBar>
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          {visibleTabs.map(({ id, renderPanel }, index) => (
            <TabPanel key={id} value={activeTab === id} index={index}>
              {renderPanel()}
            </TabPanel>
          ))}
        </div>
      </div>
    </div>
  )
}

function getTranscriptErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : fallback
}

/** Legacy `?nav=` deep links — migrated to path segments when Loop is on. */
function parseLoopNavQueryParam(search: string): LoopNavId | null {
  return parseLoopNavId(new URLSearchParams(search).get("nav"))
}

type MetricsData = {
  metrics: VirasafeMetrics | null
  numTopEngagedActivities: number | null
  numLeastDistressedActivities: number | null
}

const Mvp2ActivityMetrics: React.FC<{ metricsData: MetricsData }> = ({
  metricsData,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.activityTrends}>
      <MoodTrendsActivityStatRow insights={metricsData} />
      <div className={classes.row}>
        <InsightSummaryActivityView
          insights={metricsData}
          title={"Activities that Most Helped You Manage Your Emotions"}
          subtitle={
            "These activities had the biggest positive impact on your mood."
          }
        />
        <div className={classes.rowDivider} />
        <InsightSummaryActivityView
          insights={metricsData}
          title={"Most Engaging Activities"}
          subtitle={"You engaged with these activities most often."}
          showEngagingActivities={true}
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    clientDetail: {
      paddingTop: HeaderHeight,
      backgroundColor: colors.neutral.white,
      flex: 1,
    },
    proClientDetail: {
      paddingTop: 0,
      backgroundColor: "transparent",
      flex: "1 1 0",
      width: "100%",
      height: "100%",
      maxHeight: "100%",
      minHeight: 0,
      maxWidth: "100%",
      minWidth: 0,
      overflow: "hidden",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    },
    tabs: {
      backgroundColor: colors.neutral.white,
      color: "#545E78",
      boxShadow: "none",
      paddingLeft: 32,
      "& .MuiTabs-flexContainer": {
        gap: theme.spacing(1),
      },
      "& .MuiTab-root": {
        minWidth: "auto",
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        flexShrink: 0,
      },
      "& .MuiTabs-scrollButtons": {
        width: theme.spacing(4),
        height: theme.spacing(4),
        margin: theme.spacing(0, 0.5),
        borderRadius: "50%",
        color: colors.neutral.asphalt,
        backgroundColor: colors.primary.cloud,
        transition: theme.transitions.create(["background-color", "color"]),
        "&:hover": {
          color: colors.neutral.white,
          backgroundColor: colors.primary.periwinkle,
        },
        "&.Mui-disabled": {
          opacity: 0.35,
          backgroundColor: "transparent",
        },
      },
    },
    header: {
      maxWidth: 1440,
      margin: "0 auto",
    },
    headerTopRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    title: {
      color: colors.neutral.denim,
    },
    navigation: {
      height: 92,
      width: "fit-content",
      display: "flex",
      alignItems: "center",
      padding: 0,
      margin: 0,
      border: 0,
      backgroundColor: "transparent",
      cursor: "pointer",
      paddingLeft: theme.spacing(3),
      outline: "none",
    },
    contentContainer: {
      borderTop: `1px solid ${colors.neutral.clay}`,
      display: "flex",
      flex: 1,
      width: "100%",
      justifyContent: "center",
    },
    content: {
      maxWidth: 1440,
      padding: theme.spacing(4),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      width: "100%",
    },
    chartsAndSchedule: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: theme.spacing(3),
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
      },
    },
    // Client Detail (vira_loop): ScheduleOverview is omitted — use full width.
    chartsAndScheduleClientDetail: {
      gridTemplateColumns: "minmax(0, 1fr)",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    chart: {
      marginBottom: theme.spacing(2),
      minWidth: 0,
      maxWidth: "100%",
      // overflowX:auto steals the first vertical trackpad gesture on macOS
      // (same issue ContentTabs documents). Clip instead of creating a
      // nested scrollport inside LoopShell main.
      overflowX: "clip",
    },
    loading: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
    },
    chevron: {
      marginLeft: 4,
    },
    subtitle: {
      marginRight: 4,
    },
    titleIcon: {
      display: "flex",
      alignItems: "center",
    },
    subtitleMargin: {
      marginBottom: theme.spacing(2),
    },
    distressRatings: {
      fontWeight: 400,
    },
    activityTrends: {
      padding: theme.spacing(2),
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    widthHalf: {
      width: "50%",
    },
    row: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        flexDirection: "row",
      },
    },
    rowDivider: {
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "block",
        width: "32px",
        flexShrink: 0,
      },
    },
    connectionStatusContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      paddingLeft: theme.spacing(4),
    },
    statusChip: {
      fontWeight: 600,
    },
    disconnectedChip: {
      backgroundColor: colors.neutral.clay,
      color: colors.neutral.asphalt,
      fontWeight: 600,
    },
    closeButton: {
      margin: 0,
      padding: 0,
      border: 0,
      height: theme.spacing(2),
      width: theme.spacing(2),
      backgroundColor: "transparent",
      cursor: "pointer",
      "&:focus": {
        outline: "none",
      },
    },
    partner: {
      display: "none",
    },
    partnerTitle: {
      paddingLeft: theme.spacing(1),
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      minWidth: 0,
    },
    headerActions: {
      backgroundColor: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      paddingRight: theme.spacing(3),
      marginLeft: "auto",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        justifyContent: "space-between",
      },
    },
    ellipsisIcon: {
      display: "flex",
      width: "38px",
      height: "38px",
      padding: "10.76px 11.24px 11.24px 10.76px",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "360px",
      backgroundColor: "rgba(0, 0, 0, 0.03)",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
      },
    },
    noTabs: {
      display: "none",
    },
  })
)

export default ClientDetail
